import { Currency, useQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { toCurrencySymbol } from '@sequencehq/utils'
import { useCallback, useMemo, useState } from 'react'
import { useDefaultCurrency, useEnabledCurrencies } from 'SettingsV2/index'

type ApiOutstandingInvoices =
  DashboardApi20240730.GetInsightsOutstandingInvoices.OustandingInvoicesInsights
interface UseOutstandingInvoices {
  data: ApiOutstandingInvoices | null
  isLoading: boolean
  currencyField: {
    value: Currency
    label: Currency
    onChange: (newCurrency: string) => void
    options: { value: Currency; label: Currency }[]
  }
  valueFormat: (value: number) => string
}

export const useOutstandingInvoices = ({
  currency
}: {
  currency?: Currency | null
} = {}): UseOutstandingInvoices => {
  const res = useEnabledCurrencies()
  const enabledCurrencies = useMemo(() => {
    if (res.status === 'SUCCESS') {
      return res.enabledCurrencies
    }
    return []
  }, [res])
  const currencyOptions = useMemo(
    () =>
      enabledCurrencies.map(currency => ({
        value: currency,
        label: currency
      })),
    [enabledCurrencies]
  )

  const defaultCurrency = useDefaultCurrency()

  const [internalCurrency, setCurrency] = useState(
    currency || defaultCurrency || 'GBP'
  )

  const outstandingInvoicesQuery = useQuery(
    dashboard20240730Client.getInsightsOutstandingInvoices,
    {
      currency: internalCurrency
    }
  )

  const dataValue = outstandingInvoicesQuery.data ?? null

  const currencyField = {
    value: internalCurrency,
    label: internalCurrency,
    onChange: (newCurrency: string) => setCurrency(newCurrency as Currency),
    options: currencyOptions
  }

  const valueFormat = useCallback(
    (value: number) =>
      `${toCurrencySymbol(currencyField.value)}${Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      }).format(value)}`,
    [currencyField.value]
  )

  return {
    data: dataValue,
    isLoading: outstandingInvoicesQuery.isPending,
    currencyField,
    valueFormat
  }
}
